import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {ResetSettingButton} from '../../WorkspaceDrawer/Settings/components/ResetSettingButton';
import {SettingIndentedRow} from '../../WorkspaceDrawer/Settings/components/SettingIndentedRow';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';

export const DROPDOWN_FLEX_GROW_STYLE = {flexGrow: 1};
export const X_AXIS_DROPDOWN_RESULT_LIMIT = 100;

export type XAxisChangeHandler = (e: unknown, data: {value?: unknown}) => void;

export type AxisScaffolding = {
  xAxisRange: React.ReactNode;
  xAxisTitle: React.ReactNode;
  yAxisRange: React.ReactNode;
  yAxisTitle: React.ReactNode;
  onResetXAxis: () => void;
};

type AxisSectionsProps = AxisScaffolding & {
  xPicker: React.ReactNode;
  yPicker: React.ReactNode;
  /**
   * True when the NGW metric-search picker is in use. Its dropdown can render
   * an error/retry row below the control, so the label and reset button are
   * pinned to the control line to stay aligned with the dropdown. The legacy
   * history-keys picker is a single line and leaves this off. Passed by the
   * caller because `AxisSections` is shared by both paths.
   */
  usesMetricSearchPicker?: boolean;
};

export const AxisSections = ({
  xPicker,
  yPicker,
  xAxisRange,
  xAxisTitle,
  yAxisRange,
  yAxisTitle,
  onResetXAxis,
  usesMetricSearchPicker = false,
}: AxisSectionsProps) => {
  const xAxisLabelTooltip = (
    <Tooltip
      trigger={<SettingLabel text="X axis" />}
      content="Use step, time or any variable logged with wandb.log as xaxis."
    />
  );
  const xAxisResetButton = (
    <ResetSettingButton
      isEnabled
      onButtonClick={onResetXAxis}
      settingLevel="panel"
    />
  );
  return (
    <>
      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 pb-16', 'h-full w-full')}>
          <div
            className={twMerge('flex flex-col', 'gap-8')}
            data-test="x-axis-setting">
            <div
              className={twMerge(
                'flex w-full',
                usesMetricSearchPicker ? 'items-start' : 'items-center'
              )}>
              {usesMetricSearchPicker ? (
                <div className="flex h-40 shrink-0 items-center">
                  {xAxisLabelTooltip}
                </div>
              ) : (
                xAxisLabelTooltip
              )}
              <div
                data-test="x-axis-select"
                className={twMerge(
                  'flex w-full flex-row gap-8',
                  usesMetricSearchPicker ? 'items-start' : 'items-center'
                )}>
                {xPicker}
                {usesMetricSearchPicker ? (
                  <div className="flex h-40 items-center">
                    {xAxisResetButton}
                  </div>
                ) : (
                  xAxisResetButton
                )}
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the x-axis"
                    />
                    {xAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the x-axis"
                    />
                    <div className="flex">{xAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
          </div>
        </div>
      </Tailwind>

      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
          <div className={twMerge('flex flex-col', 'gap-8')}>
            <div className="flex w-full items-start">
              <Tooltip
                trigger={<SettingLabel text="Y axis" />}
                content="Plot any numeric metric logged with wandb.log."
              />
              <div className="flex w-full flex-row items-start gap-8">
                {yPicker}
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the y-axis"
                    />
                    {yAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the y-axis"
                    />
                    <div className="flex">{yAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
          </div>
        </div>
      </Tailwind>
    </>
  );
};
